// @flow
import React, {useMemo} from 'react';
import {Switch, Redirect} from 'react-router-dom';
import {PrivateRoute} from './PrivateRoute';

import {useUser} from '../modules/auth/utils';
import {useCompletedConfig} from '../modules/dashboard/utils/index';
import {useIsFeatureInTier} from '../utils/component';

// Containers
import HomeContainer from '../modules/home/containers/HomeContainer';
import DashboardContainer from '../modules/dashboard/containers/DashboardContainer';
import DashboardProContainer from '../modules/dashboard/containers/DashboardPlusContainer';
import ProductsContainer from '../modules/products/containers/ProductsContainer';
import ProductContainer from '../modules/products/containers/ProductContainer';
import InstagramProductsUpload from '../modules/products/components/InstagramProductsUpload';
import OrdersContainer from '../modules/orders/containers/OrdersContainer';
import OrderDetailContainer from '../modules/order/containers/OrderDetailContainer';
import ManualOrderContainer from '../modules/manual-order/containers/ManualOrderContainer';
import ConfigureContainer from '../modules/configure/containers/ConfigureContainer';
import ProfileContainer from '../modules/profile/container/ProfileContainer';
import ReferralContainer from '../modules/referrals/containers/ReferralContainer';
import NotificationsContainer from '../modules/store-notifications/container/NotificationsContainer';
import CheckoutContainer from '../modules/checkout/containers/CheckoutContainer';
import ToolsContainer from '../modules/tools/container/ToolsContainer';
import MarketingContainer from '../modules/marketing/containers/MarketingContainer';
import MyStoreContainer from '../modules/my-store/containers/MyStoreContainer';
import IntegrationsContainer from '../modules/integrations/containers/IntegrationsContainer';
import OrganizeProductsContainer from '../modules/organize-products/containers/OrganizeProductsContainer';
import CategoriesContainer from '../modules/categories/containers/CategoriesContainer';
import VariantsContainer from '../modules/variants/containers/VariantsContainer';
import EditVariantContainer from '../modules/variants/containers/EditVariantContainer';
import ProductOptionPage from '../modules/product-option/components/ProductOptionPage';
import ProductCatalogue from '../modules/product-catalogue/components';
import ProductBatchLoad from '../modules/product-batch-load/components/ProductBatchLoad';
import BulkEditCotainer from '../modules/bulk-edit/containers/BulkEditContainer';
import CustomersContainer from '../modules/crm/containers/CustomersContainer';
import CustomerContainer from '../modules/crm/containers/CustomerContainer';
import ChatContainer from '../modules/multichat/chat/containers/ChatContainer';
import FunnelsContainer from '../modules/multichat/funnels/Funnels';
import ClientContainer from '../modules/client/containers/ClientContainer';
import MultichatContacts from '../modules/multichat/contacts/components/multichat-contacts';
import CrmSegment from '../modules/crm-segment/components/crm-segment';
import CrmSegmentManualForm from '../modules/crm-segment/components/crm-segment-form/crm-segment-manual-form';
import CrmSegmentDynamicForm from '../modules/crm-segment/components/crm-segment-form/crm-segment-dynamic-form';
import CrmCampaigns from '../modules/crm-campaigns/components/crm-campaigns';
import CrmCampaignForm from '../modules/crm-campaigns/components/crm-campaign-form';
import CampaignDetail from '../modules/crm-campaigns/components/campaign-detail';
import TrainingBot from '../modules/store-bot/components/training-bot';
import StoreBot from '../modules/store-bot/components/store-bot';
import StripeTablePrice from '../modules/tiers/components/stripe-table-price';
import NotFoundPage from '../modules/navigations/components/404';
import WalletPage from '../modules/wallet/components';

import StoreChatbotConfiguration from '../modules/store-bot/components/store-chatbot-configuration';
import OrganizeProductsPage from '../modules/organize-products/components/organize-products-page';

const LoadingPlaceholder = () => null;

export function AppRoutes() {
  const user = useUser();
  const {
    completedConfig,
    isFetching: isFetchingCompletedConfig,
  } = useCompletedConfig();

  const hasProDashboardFeature = useIsFeatureInTier('DASHBOARD_PRO');
  const hasCollaboratorDashboardFeature = useIsFeatureInTier(
    'DASHBOARD_COLLABORATOR'
  );

  // Dynamic home component
  const HomeViewComponent = useMemo(() => {
    if (isFetchingCompletedConfig) return LoadingPlaceholder;

    if (!completedConfig) return HomeContainer;

    const showProDashboard =
      hasProDashboardFeature &&
      user &&
      (user.type === 'collaborator-user'
        ? hasCollaboratorDashboardFeature
        : true);

    return showProDashboard ? DashboardProContainer : DashboardContainer;
  }, [
    completedConfig,
    hasCollaboratorDashboardFeature,
    hasProDashboardFeature,
    isFetchingCompletedConfig,
    user,
  ]);

  return (
    <Switch>
      {/* Home */}
      <PrivateRoute path="/home" component={HomeViewComponent} />

      {/* Products */}
      <PrivateRoute exact path="/products" component={ProductsContainer} />
      <PrivateRoute
        exact
        path="/products/instagram-upload"
        component={InstagramProductsUpload}
      />
      <PrivateRoute
        exact
        path="/product-catalogue"
        component={ProductCatalogue}
      />
      <PrivateRoute path="/products/:productId" component={ProductContainer} />
      <PrivateRoute
        path="/products/:productId/duplicate"
        component={ProductContainer}
      />
      <PrivateRoute
        exact
        path="/products-variants"
        component={VariantsContainer}
      />
      <PrivateRoute
        path="/products-variants/:id"
        component={EditVariantContainer}
      />
      <PrivateRoute
        exact
        path="/product-option"
        component={ProductOptionPage}
      />
      <PrivateRoute path="/categories" component={CategoriesContainer} />
      <PrivateRoute
        exact
        path="/products-bulk-upload"
        component={ProductBatchLoad}
      />
      <PrivateRoute
        exact
        path="/organize-products"
        component={OrganizeProductsPage}
      />
      <PrivateRoute
        exact
        path="/organize-products-old"
        component={OrganizeProductsContainer}
      />
      <PrivateRoute
        exact
        path="/products-bulk-edit"
        component={BulkEditCotainer}
      />

      {/* Orders */}
      <PrivateRoute exact path="/orders" component={OrdersContainer} />
      <PrivateRoute
        path="/orders/manual-order"
        component={ManualOrderContainer}
      />
      <PrivateRoute path="/orders/:orderId" component={OrderDetailContainer} />

      {/* Profile */}
      <PrivateRoute exact path="/profile" component={ProfileContainer} />
      <PrivateRoute path="/profile/referral" component={ReferralContainer} />
      <PrivateRoute path="/notifications" component={NotificationsContainer} />

      {/* Configuration */}
      <PrivateRoute path="/configure" component={ConfigureContainer} />
      <PrivateRoute path="/integrations" component={IntegrationsContainer} />
      <PrivateRoute path="/my-store" component={MyStoreContainer} />
      <PrivateRoute path="/checkout" component={CheckoutContainer} />
      <PrivateRoute path="/tools" component={ToolsContainer} />
      <PrivateRoute exact path="/tiers-list" component={StripeTablePrice} />

      {/* Chat */}
      <PrivateRoute path="/chat" component={ChatContainer} />
      <PrivateRoute path="/funnels" component={FunnelsContainer} />

      {/* Marketing */}
      <PrivateRoute path="/marketing" component={MarketingContainer} />

      {/* CRM */}
      <PrivateRoute path="/client" component={ClientContainer} />
      <PrivateRoute exact path="/contacts" component={MultichatContacts} />
      <PrivateRoute exact path="/segments" component={CrmSegment} />
      <PrivateRoute
        exact
        path="/segments/manual/:id"
        component={CrmSegmentManualForm}
      />
      <PrivateRoute
        exact
        path="/segments/dynamic/:id"
        component={CrmSegmentDynamicForm}
      />
      <PrivateRoute exact path="/campaigns" component={CrmCampaigns} />
      <PrivateRoute exact path="/campaigns/:id" component={CrmCampaignForm} />
      <PrivateRoute
        exact
        path="/campaigns/detail/:id"
        component={CampaignDetail}
      />
      <PrivateRoute exact path="/customers" component={CustomersContainer} />
      <PrivateRoute
        path="/customers/:customerId"
        component={CustomerContainer}
      />

      {/* Store Bot */}
      <PrivateRoute
        path="/store-bot/training-bot/:botId"
        component={TrainingBot}
      />
      <PrivateRoute path="/store-bot/bots" component={StoreBot} />
      <PrivateRoute path="/wallet" component={WalletPage} />
      <PrivateRoute
        path="/store-bot/chatbot-configuration"
        component={StoreChatbotConfiguration}
      />

      {/* Root redirect */}
      <PrivateRoute
        exact
        path="/"
        component={() => (
          <Redirect
            to={{
              pathname: '/home',
              search: window.location.search,
            }}
          />
        )}
      />

      {/* 404 */}
      <PrivateRoute component={NotFoundPage} />
    </Switch>
  );
}
